import React, { useMemo } from 'react';
import SvgIcon from '@home/components/SvgIcon';
import { dealWithOthersName } from '@op/utils/languageValue';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import { initialUpperCase } from '@op/utils';
import useLanguage from '@op/hooks/useLanguage';
import { getReviewTypesOptions } from '@op/components/FilterCommon/constants/reviewTypes';
import { formatTimeToShow } from '@op/utils/time';

// 排序Key
const sortKeys = [
  'or_drill_language',
  'language',
  'channel',
  'region',
  'review_types',
  'exclude_channels',
  'except_language',
  'start_time',
  'reviews_rating',
  'sentiment',
  'video_tags',
] as const;

const sentimentValue: any = {
  5: 'Positive',
  3: 'Neutral',
  1: 'Negative',
};

type SortKeysType = (typeof sortKeys)[number];

export function useSubtitle({ defaultProps }: { defaultProps?: any } = {}) {
  const { formatMessage, isEn } = useLanguage();

  const translateTag = (key: string) => {
    const tagList = defaultProps?.tagList || [];
    const tag = tagList.find((val: any) => val.label_en === key);
    if (tag) {
      return isEn ? tag.label_en : tag.label_zh;
    }
    return key;
  };

  const getReviewRating = (value: string) => {
    const realValue = Array.isArray(value) ? value[0] : value;
    const numberValue = parseInt(realValue, 10);

    if (!Number.isNaN(numberValue)) {
      return (
        <>
          {Array.from({ length: numberValue }, (_, i) => (
            <SvgIcon key={i} type="star-action" />
          ))}
        </>
      );
    }

    const review = realValue ? initialUpperCase(realValue) : realValue;
    if (!review) {
      return '';
    }
    let icon = null;
    if (review === 'Positive') {
      icon = steamPositive;
    } else if (review === 'Negative') {
      icon = steamNegative;
    }

    return (
      <span className="dbo-detail-subtitle-review-rating">
        {icon && <img src={icon} alt={review} className="steam-icon" />}
        {formatMessage({ id: review })}
      </span>
    );
  };

  const getOtherRender = () => formatMessage({ id: 'Others' });

  const getNameRender = (key: 'language' | 'channel' | 'region', realKey?: string) => {
    const rKey = realKey || key;
    return (value: Array<string>) => {
      const data = value || [];
      if (data.length === 0) return null;
      return data.map((item: any) => {
        return dealWithOthersName(item, rKey);
      });
    };
  };

  function addSpecialTitle(title: any) {
    if (isEn) return title;
    return <>【{title}】</>;
  }

  const format: {
    [key in SortKeysType]: (
      value: any,
      filter: any,
      extraFilter: any,
    ) => React.ReactNode[] | React.ReactNode | string;
  } = useMemo(() => {
    return {
      language: getNameRender('language'),
      or_drill_language: getNameRender('language', "or_drill_language"),
      channel: getNameRender('channel'),
      region: getNameRender('region'),
      review_types: (value: string[]) => {
        if (!value?.length) return null;
        const optionMap = Object.fromEntries(
          getReviewTypesOptions(true).map((item) => [item.value, item.text]),
        );
        return value
          .map((item) => formatMessage({ id: optionMap[item] || item }))
          .join(', ');
      },
      exclude_channels: (value) => {
        if (!value?.length) return null;
        return getOtherRender();
      },
      except_language: (value) => {
        if (!value?.length) return null;
        return getOtherRender();
      },
      sentiment: (value: string) => {
        return formatMessage({ id: sentimentValue[value] });
      },
      reviews_rating: getReviewRating,
      start_time: (value, filter = {}, extraFilter) => {
        return `${formatTimeToShow({
          ...filter,
          date_type: extraFilter.date_type || 'daily',
        })}`;
      },
      video_tags: (value: Array<{ t1: string; t2: string[] }>) => {
        if (!value?.length) return null;
        return value
          .map((tag) => {
            if (tag.t2?.length > 0) {
              return tag.t2.map((t) => translateTag(t)).join(', ');
            }
            return translateTag(tag.t1);
          })
          .join(' | ');
      },
    };
  }, [formatMessage, defaultProps, isEn]);

  return { format, addSpecialTitle, sortKeys };
}
